import { CheckmarkOutline, ChevronLeft, Edit, FaceDissatisfied, FaceSatisfied, Information, Locked, Reply, TrashCan, User, ViewFilled } from "@carbon/icons-react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { ScrollView} from "react-native-web";
import { GUIDELINES } from "feature/question/community-guidelines";
import { DocumentLevelTooltip, InfoTag, ToolTip } from 'open-modules/zdf/component/tooltip';
import { useParentDatastore } from 'open-modules/zdf/component/parentdatastore';
import { Datastore, useDatastore, useModuleObject, usePersonaKey, usePersonaObject, useServerCallResult } from "util/datastore";
import { colorBorderFacepile, colorBorderSelected, colorSurfacePrimary, colorTextHighlight, colorTextHint, colorTextOnDarkBackground, colorTextOnLightColorTag, colorTextPrimary, colorTextSecondary, colorTextSecondaryOnLightColorBg, colorTextSuccess, colorTextWarning } from 'component/color';
import { CTAButton, SubtleButton, TextButton } from 'component/button';
import { HorizBox, Pad, PadBox, Separator } from 'component/basics';
import { Heading, TextField, UtilityText } from 'component/text';
import { Checkbox, Toggle } from 'component/form';
import { Banner } from 'component/banner';
import { Modal } from 'component/modal';
import { FaceImage, LetterFace } from 'component/people';
import { useInstanceParams } from "util/params";
import { setToastMessage } from 'open-modules/zdf/component/toast';
import { translateLabel } from 'component/translation';
import { logEventAsync } from "util/eventlog";
import { CTAButtonWithLoadingSpinner } from 'open-modules/zdf/component/button';
import { ToggleWithLabelWidget } from 'open-modules/zdf/component/form';
import { JudgementContextType, JudgementType, ModTask } from "open-modules/zdf/component/moddashboard/moddashboardtypes";
import { moduleMapChannel, moduleMapInstanceToChannel } from "open-modules/zdf/component/channels/channeltypes";
import { undefS, t } from "util/stdtypes";
import { setBlockStatusAsync, useUserSuspensions, setWarnStatusAsync }  from "open-modules/zdf/component/suspension/suspension";


const JudgementFlowState = {
    JUDGE: "JUDGE",
    REJECTREASON: "REJECTREASON",
    AFTERAPPROVE: "AFTERAPPROVE",
}

const JudgementContext = createContext<JudgementContextType>({} as JudgementContextType);

type SetJudgementArgs = {
    judgement: string;
    datastore: Datastore;
    modTask: any;
    reasoning?: string;
    customMessage?: string | null;
    shouldWarn?: boolean;
    shouldBlock?: boolean;
};

async function setJudgementAsync({ judgement, datastore, modTask, reasoning, customMessage, shouldWarn = false, shouldBlock = undefined }: SetJudgementArgs) {
    logEventAsync(datastore, 'moderation-human-judgement', { judgement, commentKey: modTask.key });
    await datastore.callServerAsync('moderation', 'setJudgement', {
        key: modTask.key, judgement, reasoning: reasoning || modTask.reasoning, customMessage, shouldWarn, shouldBlock
    });
}

const JudgementCardStyle = StyleSheet.create({
    elevatedCard: {
        backgroundColor: colorSurfacePrimary,
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 8,
        padding: 16,
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)" as any, // FIXME: boxShadow not typed for native
        borderColor: colorBorderFacepile,
        borderWidth: 1,
        gap: 12
    },
    guidelineColumn: {
        margin: 4,
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        justifyContent: "space-evenly",
        gap: 12,
    }
});

type GetChannelKeyArgs = { modTask: any; datastore: Datastore };

async function getChannelKeyFromModTaskAsync({ modTask, datastore }: GetChannelKeyArgs): Promise<string | undefined> {
    const channelInstanceMappingEntryMap = await datastore.getModuleObjectAsync(
        moduleMapInstanceToChannel,
        modTask.structureKey + "/" + modTask.instanceKey
    );
    const channelKey = channelInstanceMappingEntryMap ? Object.keys(channelInstanceMappingEntryMap)[0] : undefS;
    return channelKey;
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function JudgementCard({ modTask, modAuthor, taskWatchState, isSelected, isWarnDisabled = false, onCollapse }) {
    const s = JudgementCardStyle;

    const datastore = useDatastore();
    const parentDatastore = useParentDatastore();

    const amIViewing = taskWatchState?.amIViewing;
    const [pageIndex, setPageIndex] = useState(JudgementFlowState.JUDGE);
    const [isReduced, setIsReduced] = useState(false);
    const [shouldBlock, setShouldBlock] = useState(undefined as boolean | undefined);
    const [judgementProcessing, setJudgementProcessing] = useState(false);
    const [editorialCommentProcessing, setEditorialCommentProcessing] = useState(false);
    const [shouldShowEditorialCommentModal, setShouldShowEditorialCommentModal] = useState(false);
    const [channelKey, setChannelKey] = useState(undefS);
    const [isReplyDisabled, setIsReplyDisabled] = useState(!channelKey);

    const judgementContext: JudgementContextType = useMemo(() => ({
        modTask, modAuthor, channelKey, taskWatchState, amIViewing, isSelected, isWarnDisabled, isReplyDisabled, setIsReplyDisabled, isReduced, shouldBlock, setShouldBlock, judgementProcessing, setJudgementProcessing, editorialCommentProcessing, setEditorialCommentProcessing, pageIndex, setPageIndex, shouldShowEditorialCommentModal, setShouldShowEditorialCommentModal, onCollapse, approveModTaskAsync
    }), [
        modTask, modAuthor, channelKey, taskWatchState, amIViewing, isSelected, isWarnDisabled, isReplyDisabled, isReduced, shouldBlock, judgementProcessing, editorialCommentProcessing, pageIndex, shouldShowEditorialCommentModal, onCollapse
    ]);

    useEffect(() => {
        async function getChannelKeyAsync() {
            const channelKey = await getChannelKeyFromModTaskAsync({ modTask, datastore });
            setChannelKey(channelKey);
            setIsReplyDisabled(!channelKey);
        }
        getChannelKeyAsync();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= window.screen.availWidth / 1.3) {
                setIsReduced(true);
            } else {
                setIsReduced(false);
            }
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    async function approveModTaskAsync() {
        try {
            setJudgementProcessing(true);
            // Reset warning for this comment if there is one to avoid giving multiple warnings per comment
            if (modTask?.receivedWarning) {
                const userId = modAuthor.authorKey;
                await setWarnStatusAsync({
                    datastore: datastore,
                    userKey: userId,
                    modTaskKey: modTask.key,
                    value: false
                })
            }
            await setJudgementAsync({ judgement: "approve", datastore, modTask });
            setToastMessage({
                datastore: parentDatastore,
                label: t("The comment has been successfully approved"),
                visible: true,
            });
            onCollapse && onCollapse();
        } catch (e) {
            setToastMessage({
                datastore: parentDatastore,
                label: t("Operation could not be executed, please try again"),
                visible: true,
                type: "error",
            });
        } finally {
            setJudgementProcessing(false);
        }
    }

    // Judgement card is visible if the user has selected it or someone else is working on it
    return (
        <JudgementContext.Provider value={judgementContext}>
            {(taskWatchState || isSelected) && (
                <ToolTip
                    label={t("Only one moderator at a time can work on a comment.")}
                    disabled={!taskWatchState || amIViewing || !isSelected}
                >
                    <View
                        style={[
                            s.elevatedCard,
                            {
                                borderColor: isSelected ? colorBorderSelected : colorBorderFacepile,
                            },
                        ]}
                        testID={"judgementCard-" + modTask.key}
                    >
                        {pageIndex === JudgementFlowState.JUDGE && (
                            <>
                                {taskWatchState && <ModTaskViewerInfo />}
                                {isSelected && <ModTaskJudgementMenu />}
                            </>
                        )}
                        {pageIndex === JudgementFlowState.REJECTREASON && (
                            <ModTaskRejectionMenu
                                onBack={() => setPageIndex(JudgementFlowState.JUDGE)}
                                onNext={() => setPageIndex(JudgementFlowState.JUDGE)}
                            />
                        )}
                        {pageIndex === JudgementFlowState.AFTERAPPROVE && (
                            <ModTaskApprovalMenu
                                onBack={() => setPageIndex(JudgementFlowState.JUDGE)}
                                onNext={() => setPageIndex(JudgementFlowState.JUDGE)}
                            />
                        )}
                    </View>
                    {shouldShowEditorialCommentModal && <EditorialCommentModal />}
                </ToolTip>
            )}
        </JudgementContext.Provider>
    );
}

function ModTaskViewerInfo() {
    const { amIViewing, taskWatchState, isReduced } = useContext(JudgementContext);

    return (
        <HorizBox center gap={8}>
            <View>
                {taskWatchState?.photoUrl ? (
                    <FaceImage type={"tiny"} photoUrl={taskWatchState?.photoUrl} />
                ) : (
                    <LetterFace type={"tiny"} name={taskWatchState?.name} hue={taskWatchState?.hue} />
                )}
            </View>
            <UtilityText
                color={colorTextHighlight}
                label={amIViewing ? t("You are reviewing") : t("{viewerName} is reviewing")}
                formatParams={{
                    viewerName: !isReduced ? taskWatchState?.name ?? "" : getShortenedName(taskWatchState?.name ?? ""),
                }}
                type="tiny"
                weight="medium"
            />
            {!isReduced && (
                <View style={{ width: 16, height: 16 }}>
                    {amIViewing ? <ViewFilled color={colorTextHighlight} /> : <Locked color={colorTextHighlight} />}
                </View>
            )}
        </HorizBox>
    );
}

function ModTaskJudgementMenu() {
    const { modTask, taskWatchState, amIViewing, setPageIndex, judgementProcessing } = useContext(JudgementContext);

    async function onPressApprove() {
        setPageIndex(JudgementFlowState.AFTERAPPROVE);
    }

    async function onPressReject() {
        setPageIndex(JudgementFlowState.REJECTREASON);
    }

    return (
        <>
            {(modTask.judgement === "reject" || !modTask.judgement) && (
                <CTAButtonWithLoadingSpinner
                    icon={<FaceSatisfied style={{ fill: colorTextSuccess }} />}
                    label={t("Approve")}
                    type="secondary"
                    onPress={onPressApprove}
                    disabled={(taskWatchState && !amIViewing) || judgementProcessing}
                    wide
                    processing={judgementProcessing}
                />
            )}
            {(modTask.judgement === "approve" || !modTask.judgement) && (
                <CTAButton
                    icon={<FaceDissatisfied style={{ fill: colorTextWarning }} />}
                    label={t("Reject")}
                    type="secondary"
                    onPress={onPressReject}
                    disabled={(taskWatchState && !amIViewing) || judgementProcessing}
                    wide
                />
            )}
            {modTask.judgement === "approve" && <EditorialCommentButton />}
        </>
    );
}

const CustomRejectionMessageButtonStyle = StyleSheet.create({
    responseContainer: {
        flexDirection: "column",
    },
    messagePreview: {
        width: 250
    },
    actionButtons: {
        justifyContent: "flex-end",
        flexDirection: "row",
        alignContent: "center"
    }
});

export function CustomRejectionMessageButton({ message, setMessage }) {
    const s = CustomRejectionMessageButtonStyle;
    const [shouldShowModal, setShouldShowModal] = useState(false);
    return (
        <View>
            {message === null ? (
                <CTAButton label={t("Add explanation to user")} type="secondary" onPress={() => setShouldShowModal(true)} />
            ) : (
                <View style={s.responseContainer}>
                    <Separator />
                    <Pad size={12} />
                    <HorizBox spread>
                        <HorizBox gap={4}>
                            <CheckmarkOutline />
                            <UtilityText label={t("Explanation to user")} weight="bold" />
                        </HorizBox>
                    </HorizBox>
                    <Pad size={8} />
                    <View style={s.messagePreview}>
                        <UtilityText text={message} color={colorTextSecondary} numberOfLines={2} />
                    </View>
                    <Pad size={8} />
                    <View style={s.actionButtons}>
                        <TextButton leftIcon={TrashCan} label={t("Delete")} onPress={() => setMessage(null)} />
                        <Pad size={24} />
                        <TextButton leftIcon={Edit} label={t("Edit")} onPress={() => setShouldShowModal(true)} />
                    </View>
                </View>
            )}
            {shouldShowModal && (
                <CustomRejectionMessageModal setMessage={setMessage} message={message} setShouldShow={setShouldShowModal} />
            )}
        </View>
    );
}

const CustomRejectionMessageModalStyle = StyleSheet.create({
    modalButtonRow: {
        width: "100%",
        gap: 16,
        justifyContent: "center",
        flexDirection: "row",
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function CustomRejectionMessageModal({ setMessage, setShouldShow, message }) {
    const s = CustomRejectionMessageModalStyle;
    const [internalMessage,setInternalMessage] = useState(message)
    return (
        <Modal
            showTopBar={false}
            onClose={() => setShouldShow(false)}
            buttonRow={
                <View style={s.modalButtonRow}>
                    <CTAButton type="secondary" label={t("Cancel")} wide onPress={() => setShouldShow(false)} />
                    <CTAButton
                        label={t("Save")}
                        onPress={() => {
                            setShouldShow(false);
                            setMessage(internalMessage);
                        }}
                        wide
                    />
                </View>
            }
        >
            <PadBox vert={32} horiz={20}>
                <Heading label={t("Add explanation to user")} type="large" weight="medium" />
                <Pad size={8} />
                <UtilityText
                    label={t("Write an additional explanation for the user about your rejection.")}
                    color={colorTextSecondary}
                />
                <Pad size={8} />
                <TextField
                    multiline={true}
                    testID="textfield-explanation-to-user"
                    value={internalMessage}
                    big
                    onChange={(content) => {
                        if (content !== "") {
                            setInternalMessage(content);
                        } else {
                            setInternalMessage(null);
                        }
                    }}
                />
                <Pad size={12} />
                <UtilityText
                    label={t("Explanations are sent directly to the user by e-mail after you have rejected.")}
                    color={colorTextSecondary}
                />
            </PadBox>
        </Modal>
    );
}

function EditorialCommentButton() {
    const { modTask, judgementProcessing, isReplyDisabled, taskWatchState, amIViewing, setShouldShowEditorialCommentModal } = useContext(JudgementContext);
    
    return (
        <ToolTip
            label={
                modTask?.deleted
                    ? t("You can't reply to a deleted comment.")
                    : t("You can't reply to this comment because it doesn't belong to a channel.")
            }
            disabled={!isReplyDisabled && !modTask?.deleted}
        >
            <CTAButton
                icon={<Reply color={colorTextPrimary} />}
                label={t("Reply")}
                type="secondary"
                onPress={() => setShouldShowEditorialCommentModal(true)}
                disabled={(taskWatchState && !amIViewing) || judgementProcessing || isReplyDisabled || modTask.deleted}
                wide
            />
        </ToolTip>
    );
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function EditorialCommentModal() {
    const s = CustomRejectionMessageModalStyle;
    const datastore = useDatastore();
    const parentDatastore = useParentDatastore();
    const { modTask, channelKey, editorialCommentProcessing, setEditorialCommentProcessing, setShouldShowEditorialCommentModal, approveModTaskAsync, setPageIndex } = useContext(JudgementContext);
    const channel = useModuleObject(moduleMapChannel, channelKey ?? null);
    const [commentText, setCommentText] = useState("");
    const [comment, setComment] = useState(() => ({ text: "", key: datastore.getNewKey(), replyTo: modTask?.key, from: "", time: Date.now() }));

    async function onPressPost() {
        await postCommentAsChannelAsync();
    }

    async function postCommentAsChannelAsync() {
        setEditorialCommentProcessing(true);

        const channelUserResult = await datastore.callServerAsync("channel", "getChannelFakeUser", {
            channelKey,
        });

        if (!channelUserResult) {
            setToastMessage({
                datastore: parentDatastore,
                label: t("Failed to post reply"),
                visible: true,
                type: "error",
            });
        } else {
            const commentToPost = { ...comment, text: commentText, from: channelUserResult.key, time: Date.now() };
            setComment({ ...commentToPost });

            await datastore.callServerAsync("channel", "postCommentAsChannelUser", {
                structureKey: modTask.structureKey,
                instanceKey: modTask.instanceKey,
                comment: commentToPost,
                persona: channelUserResult,
            });

            // Create mod task for editorial comment
            await datastore.callServerAsync("moderationZdf", "autoApprove", {
                type: "comment",
                key: comment.key,
            });

            if (modTask.judgement !== JudgementType.APPROVE) {
                // TODO: How to avoid triggering the loading spinner of the "No thanks" button here since that is also connected to approving the mod task?
                await approveModTaskAsync();
                setPageIndex(JudgementFlowState.JUDGE);
            }

            setToastMessage({
                datastore: parentDatastore,
                label: t("Your reply has been successfully posted"),
                visible: true,
            });
        }

        setEditorialCommentProcessing(false);
        setShouldShowEditorialCommentModal(false);
    }

    return (
        <Modal
            showTopBar={false}
            onClose={() => setShouldShowEditorialCommentModal(false)}
            buttonRow={
                <View style={s.modalButtonRow}>
                    <CTAButton
                        type="secondary"
                        label={t("Cancel")}
                        wide
                        onPress={() => setShouldShowEditorialCommentModal(false)}
                    />
                    <CTAButtonWithLoadingSpinner
                        label={t("Post")}
                        onPress={onPressPost}
                        wide
                        processing={editorialCommentProcessing}
                    />
                </View>
            }
        >
            <PadBox vert={32} horiz={20}>
                <Heading label={t("Add a reply")} type="large" weight="medium" />
                <Pad size={8} />
                <UtilityText
                    label={
                        channel?.name
                            ? t("Write a public reply to this comment as {channel}. This will be displayed in the comments for everyone.")
                            : t("Write a public reply to this comment as your channel. This will be displayed in the comments for everyone.")
                    }
                    formatParams={{ channel: channel?.name ?? null }}
                    color={colorTextSecondary}
                />
                <Pad size={32} />
                <UtilityText label={t("Reply")} weight="medium" />
                <Pad size={12} />
                <TextField
                    multiline={true}
                    testID="textfield-editorial-comment"
                    value={commentText}
                    big
                    onChange={(content) => {
                        if (content !== "") {
                            setCommentText(content);
                        } else {
                            setCommentText("");
                        }
                    }}
                />
            </PadBox>
        </Modal>
    );
}



function ModTaskApprovalMenu({ onBack, onNext }) {
    const s = JudgementCardStyle;
    const { judgementProcessing, approveModTaskAsync } = useContext(JudgementContext);

    async function onPressNoThanks() {
        await approveModTaskAsync();
        onNext();
    }

    return (
        <View style={s.guidelineColumn}>
            <SubtleButton icon={ChevronLeft} label={t("Back to selection")} onPress={onBack} />
            <UtilityText label={t("Do you want to do anything else?")} weight="medium" />
            <EditorialCommentButton />
            <CTAButtonWithLoadingSpinner
                wide
                label={t("No thanks")}
                onPress={onPressNoThanks}
                processing={judgementProcessing}
            />
        </View>
    );
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function ModTaskRejectionMenu({ onBack, onNext }) {
    const s = JudgementCardStyle;

    const datastore = useDatastore();
    const parentDatastore = useParentDatastore();
    const { modTask, modAuthor, onCollapse, isWarnDisabled, shouldBlock, judgementProcessing, setJudgementProcessing } = useContext(JudgementContext);
    const guidelineNames = [...Object.values(GUIDELINES), 'Other'];
    const userId = modAuthor.authorKey;
    const currentModeratorKey = usePersonaKey();
    const currentModeratorPersona = usePersonaObject(currentModeratorKey);

    const [shouldWarn, setShouldWarn] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [customMessage, setCustomMessage] = useState(null);
    const [selectedGuidelines, setSelectedGuidelines] = useState(() => selectInitialGuidelines());
    const maybeSortedGuidelines = guidelineNames.slice().sort((a) => (selectedGuidelines[a] ? -1 : 1));

    const suspensionInfo = useUserSuspensions({userKey: userId})
    const warningCount = suspensionInfo.activeWarnings.length
    

    useEffect(() => {
        async function fetchBlockStatus() {
            const blocked = await datastore.callServerAsync("suspension", "getIsUserBlocked", { userKey: userId });
            setIsBlocked(blocked);
        }
        fetchBlockStatus();
    }, [userId]);

    function selectInitialGuidelines(){
        const initialSelection = {}
        if (modTask.violations) {
            guidelineNames.forEach(guidelineName => {
                initialSelection[guidelineName] = modTask.violations?.includes(guidelineName);
            });
        }
        return initialSelection;
    }

    // FIXME: This function is too large and should be broken up
    /* eslint-disable-next-line max-lines-per-function */
    async function onRejectAsync() {
        // Check if user is already blocked. If they are, skip the warn & block dialog and finish rejecting the comment immediately.
        // If not, set judgement and reasoning but don't update the queue yet. Otherwise, the comment will disappear from the current filter category before the warn/block dialog can be shown.
        setJudgementProcessing(true);
        const reasoningsUntranslated = Object.entries(selectedGuidelines)
            .filter((value) => value[1] === true)
            .map((value) => value[0]);
        const reasoningTranslated = reasoningsUntranslated.map((reasoning) =>
            translateLabel({ label: reasoning, language: datastore.getLanguage() })
        );

        const preliminaryJudgement = {
            judgement: "reject",
            datastore,
            modTask,
            reasoning: JSON.stringify(reasoningTranslated),
            customMessage: customMessage !== undefined ? customMessage : null,
            shouldWarn,
            shouldBlock,
        };

        try {
            if (shouldBlock) {
                await setBlockStatusAsync({ datastore, userKey: userId, value: true, shouldSendEmail: false });
            }

            // We need to warn the user first, then process the comment judgement to make sure the number of warnings in the judgement email is up-to-date.
            if (shouldWarn) {
                await setWarnStatusAsync({
                    datastore: datastore,
                    userKey: userId,
                    modTaskKey: modTask.key,
                    value: true,
                });
            }

            await setJudgementAsync(preliminaryJudgement);

            if (shouldBlock) {
                setToastMessage({
                    datastore: datastore,
                    label: t("The comment has been successfully rejected and \n{user} was successfully blocked"),
                    visible: true,
                    formatParams: { user: modAuthor.authorName },
                });
            } else if (shouldWarn) {
                setToastMessage({
                    datastore: parentDatastore,
                    label: t("The comment has been successfully rejected and \n{user} was successfully warned"),
                    visible: true,
                    formatParams: { user: modAuthor.authorName },
                });
            } else {
                setToastMessage({
                    datastore: parentDatastore,
                    label: t("The comment has been successfully rejected"),
                    visible: true,
                });
            }

            onCollapse && onCollapse();
            onNext();
        } catch (error) {
            setToastMessage({
                datastore: parentDatastore,
                label: t("Operation could not be executed, please try again"),
                visible: true,
                type: "error",
            });
        } finally {
            setJudgementProcessing(false);
        }
    }

    return (
        <View style={s.guidelineColumn}>
            <SubtleButton icon={ChevronLeft} label={t("Back to selection")} onPress={onBack} />
            <UtilityText label={t("Which Community Guideline does this comment violate?")} weight="medium" />
            <ScrollableGuidelineSelector
                guidelines={maybeSortedGuidelines}
                selectedGuidelines={selectedGuidelines}
                setSelectedGuidelines={setSelectedGuidelines}
            />
            <CustomRejectionMessageButton message={customMessage} setMessage={setCustomMessage} />
            <Separator />
            <WarnUserToggle shouldWarn={shouldWarn} setShouldWarn={setShouldWarn} disabled={isWarnDisabled} />
            {(warningCount >= 2 || isBlocked) && <JudgementCardBlockWidget modAuthor={modAuthor} />}
            {warningCount >= 1 && <WarningCounterBanner userId={userId} warningCount={warningCount} />}
            <CTAButtonWithLoadingSpinner
                wide
                label={t("Reject")}
                disabled={Object.values(selectedGuidelines).filter((s) => s === true).length <= 0}
                onPress={onRejectAsync}
                processing={judgementProcessing}
            />
        </View>
    );
}

function ScrollableGuidelineSelector({ guidelines, selectedGuidelines, setSelectedGuidelines }) {
    return (
        <ScrollView style={{ height: 250 }}>
            {guidelines.map((guidelineName) => (
                <PadBox horiz={8} key={"padbox-" + guidelineName}>
                    <Checkbox
                        label={guidelineName}
                        key={guidelineName}
                        value={selectedGuidelines[guidelineName]}
                        onChange={(isSelected) => {
                            setSelectedGuidelines((selection) => ({ ...selection, [guidelineName]: isSelected }));
                        }}
                    />
                </PadBox>
            ))}
        </ScrollView>
    );
}

function WarnUserToggle({ shouldWarn, setShouldWarn, disabled }) {
    function toggleWarning() {
        if (!disabled) {
            setShouldWarn(!shouldWarn);
        }
    }

    return disabled ? (
        <ToolTip label={t("This feature is disabled.")}>
            <ToggleWithLabelWidget
                labelWidget={WarnUserToggleLabelWidget}
                value={shouldWarn}
                onChange={toggleWarning}
                testID={"warn-toggle"}
                ariaText={t("Warn user")}
                spread
                disabled={true}
            />
        </ToolTip>
    ) : (
        <ToggleWithLabelWidget
            labelWidget={WarnUserToggleLabelWidget}
            value={shouldWarn}
            onChange={toggleWarning}
            testID={"warn-toggle"}
            ariaText={t("Warn user")}
            spread
        />
    );
}

function WarnUserToggleLabelWidget() {
    return (
        <View style={{ flexDirection: "row", alignItems: "center" }}>
            <UtilityText label={t("Warn user")} />
            <Pad size={8} />
            <InfoTag label={t("The user receives an automatic message by e-mail after the warning. It is possible to cancel the warning.")} />
        </View>
    );
}

function WarningCounterBanner({ userId, warningCount }) {
    const params = useInstanceParams();
    const datastore = useDatastore();
    const parentDatastore = useParentDatastore();

    function onProfile() {
        if (!params) return;
        params.userId = userId;
        datastore.gotoInstance({
            structureKey: "moddashboard",
            instanceKey: parentDatastore.props.instanceKey,
            params: params,
        });
    }

    return (
        <Banner>
            <UtilityText
                label={t("User has already been warned {count} {noun}.")}
                formatParams={{ count: warningCount, singular: "time", plural: "times" }}
                color={colorTextSecondaryOnLightColorBg}
            />
            <Pad size={16} />
            <Separator />
            <Pad size={16} />
            <HorizBox>
                <User />
                <Pad size={4} />
                <TextButton type="small" color={colorTextSecondaryOnLightColorBg} strong label={t("Show user profile")} onPress={onProfile} />
            </HorizBox>
        </Banner>
    );
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function JudgementCardBlockWidget({ modAuthor }) {
    const datastore = useDatastore();
    const userId = modAuthor.authorKey;

    const [showBlockConfirmationModal, setShowBlockConfirmationModal] = useState(false);
    const [showUnblockConfirmationModal, setShowUnblockConfirmationModal] = useState(false);

    const [isBlocked, setIsBlocked] = useState(false);
    const { shouldBlock, setShouldBlock } = useContext(JudgementContext);

    async function onConfirmBlock() {
        setShouldBlock(true);
        setShowBlockConfirmationModal(false);
    }

    async function onConfirmUnblock() {
        setShouldBlock(false);
        setShowUnblockConfirmationModal(false);
    }

    async function resetConfirmation() {
        setShouldBlock(undefined);
    }

    useEffect(() => {
        async function fetchBlockStatus() {
            const blocked = await datastore.callServerAsync("suspension", "getIsUserBlocked", { userKey: userId });
            setIsBlocked(blocked);
        }
        fetchBlockStatus();
    }, [userId]);

    return <>
        <HorizBox spread center>
            <HorizBox center>
                <UtilityText label={t("Block user")} />
                <Pad size={8} />
                <InfoTag label={t("Blocking prevents the user from posting further content. This action can be undone at any time if needed.")} />
            </HorizBox>
            <Toggle
                value={isBlocked && shouldBlock === undefined ? isBlocked : shouldBlock}
                onChange={(shouldBlock: boolean) => {
                    if (shouldBlock && !isBlocked) {
                        setShowBlockConfirmationModal(true);
                    } else if (!shouldBlock && isBlocked) {
                        setShowUnblockConfirmationModal(true);
                    }
                    else {
                        resetConfirmation();
                    }
                }}
                testID={"block-toggle"}
            />
        </HorizBox>

        {showBlockConfirmationModal && (
            <BlockConfirmationModal
                onBlock={() => {
                    onConfirmBlock();
                }}
                onClose={() => setShowBlockConfirmationModal(false)}
            />
        )}

        {showUnblockConfirmationModal && (
            <UnblockConfirmationModal
                onUnblock={() => {
                    onConfirmUnblock();
                }}
                onClose={() => setShowUnblockConfirmationModal(false)}
            />
        )}
    </>
}

export function BlockConfirmationModal({ onBlock, onClose }) {
    return (
        <Modal onClose={onClose}>
            <PadBox vert={40} horiz={20}>
                <Heading label={t("Block this user?")} type="large" />
                <Pad size={8} />
                <UtilityText
                    label={t("Do you really want to block this user? You can unblock the user via the profile page.")}
                    color={colorTextSecondary}
                />
                <Pad size={32} />
                <CTAButton
                    wide
                    type="delete"
                    label={t("Block user")}
                    onPress={async () => {
                        onBlock();
                    }}
                />
            </PadBox>
        </Modal>
    );
}

export function UnblockConfirmationModal({ onUnblock, onClose }) {
    return (
        <Modal onClose={onClose}>
            <PadBox vert={40} horiz={20}>
                <Heading label={t("Unblock this user?")} type="large" />
                <Pad size={8} />
                <UtilityText label={t("Do you really want to unblock this user?")} color={colorTextSecondary} />
                <Pad size={32} />
                <CTAButton
                    wide
                    type="delete"
                    label={t("Unblock user")}
                    onPress={async () => {
                        onUnblock();
                    }}
                />
            </PadBox>
        </Modal>
    );
}

function getShortenedName(name) {
    if (!name) return ""; 
    const parts = name.split(" ");
    if (parts.length === 1) {
        return parts[0];
    }

    const firstName = parts[0];
    const lastName = parts[parts.length - 1];

    const lastNameParts = lastName.split("-");
    const abbreviatedLastName = lastNameParts
        .map(part => `${part[0]}.`)
        .join("-");

    return `${firstName} ${abbreviatedLastName}`.trim();
} 